// src/components/proposal-formats/FormatDepositRulesEditor.tsx

import { useCallback, useEffect, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  getFormatDepositRule,
  upsertFormatDepositRule,
} from "@/lib/opportunity/formatDepositRuleClient";
import {
  DEFAULT_FORMAT_DEPOSIT_RULE,
  type FormatDepositMode,
  type FormatDepositRule,
} from "@/lib/opportunity/formatDepositTypes";
import type { DepositComputation } from "@/lib/opportunity/formatDepositEval";
import { formatOpportunityCurrency } from "@/lib/opportunity/formatCurrency";

export type FormatDepositRulesEditorProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  depositPreview: DepositComputation;
  reloadTick: number;
  onSaved: () => void;
  embedded?: boolean;
};

function ruleToDraft(rule: FormatDepositRule | null) {
  const src = rule ?? DEFAULT_FORMAT_DEPOSIT_RULE;
  return {
    deposit_mode: src.deposit_mode,
    deposit_value: String(src.deposit_value),
  };
}

export function FormatDepositRulesEditor(props: FormatDepositRulesEditorProps) {
  const { formatKey, productType, depositPreview, reloadTick, onSaved, embedded } = props;
  const [stored, setStored] = useState<FormatDepositRule | null>(null);
  const [draft, setDraft] = useState(ruleToDraft(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getFormatDepositRule({ format_key: formatKey, product_type: productType });
    setLoading(false);
    if (r.ok) {
      setStored(r.data.rule);
      setDraft(ruleToDraft(r.data.rule));
    }
  }, [formatKey, productType]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const dirty =
    draft.deposit_mode !== (stored?.deposit_mode ?? DEFAULT_FORMAT_DEPOSIT_RULE.deposit_mode) ||
    draft.deposit_value !== String(stored?.deposit_value ?? DEFAULT_FORMAT_DEPOSIT_RULE.deposit_value);

  async function handleSave() {
    const val = Number(draft.deposit_value);
    if (!Number.isFinite(val) || val < 0) return;
    if (draft.deposit_mode === "percent" && val > 100) return;
    setSaving(true);
    setMsg(null);
    const r = await upsertFormatDepositRule({
      format_key: formatKey,
      product_type: productType,
      deposit_mode: draft.deposit_mode,
      deposit_value: val,
    });
    setSaving(false);
    if (r.ok) {
      setStored(r.data.rule);
      setMsg({ ok: true, text: "Deposit rules saved." });
      onSaved();
    } else {
      setMsg({ ok: false, text: r.error });
    }
  }

  const shellClass = embedded
    ? "space-y-3"
    : "rounded-2xl border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-3";

  return (
    <section className={shellClass}>
      {embedded ? null : (
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Deposit rules
        </h4>
      )}
      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <>
          <p className="text-[11px] text-gray-500 dark:text-zinc-400">
            Deposit % or $ applied to the deposit base. Milestone labels live in the payment
            schedule section below.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={draft.deposit_mode}
              onChange={(e) =>
                setDraft((d) => ({ ...d, deposit_mode: e.target.value as FormatDepositMode }))
              }
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
            >
              <option value="percent">%</option>
              <option value="amount">$</option>
            </select>
            <input
              type="number"
              min={0}
              max={draft.deposit_mode === "percent" ? 100 : undefined}
              step="0.01"
              value={draft.deposit_value}
              onChange={(e) => setDraft((d) => ({ ...d, deposit_value: e.target.value }))}
              className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
            />
            <span className="text-xs text-gray-500">
              of deposit base{" "}
              <strong className="tabular-nums">
                {formatOpportunityCurrency(depositPreview.depositBase)}
              </strong>
              {depositPreview.excludedFromDeposit > 0 ? (
                <span>
                  {" "}
                  (excludes {formatOpportunityCurrency(depositPreview.excludedFromDeposit)} from
                  lines)
                </span>
              ) : null}
            </span>
          </div>

          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs border-t border-gray-100 dark:border-zinc-800 pt-2">
            <dt className="text-gray-500">Preview deposit</dt>
            <dd className="text-right tabular-nums font-medium">
              {formatOpportunityCurrency(depositPreview.depositAmount)}
            </dd>
            <dt className="text-gray-500">Preview balance</dt>
            <dd className="text-right tabular-nums font-medium">
              {formatOpportunityCurrency(depositPreview.balanceAmount)}
            </dd>
          </dl>

          <div className="flex items-center justify-end gap-2">
            {msg ? (
              <span className={msg.ok ? "text-emerald-600 text-xs" : "text-red-600 text-xs"}>
                {msg.text}
              </span>
            ) : null}
            <button
              type="button"
              disabled={saving || !dirty}
              onClick={() => void handleSave()}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save deposit rules"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
